import {
  Link,
  Text,
  Wrap,
} from '@chakra-ui/react';
import {
  useBoardings,
  usePassengerKms,
  useServiceKms,
} from '@src/modules/performance/context/reports-api-hooks';

import StackedAreaChart from '@src/modules/performance/components/charts/StackedAreaChart';
import LineChart from '@src/modules/performance/components/charts/LineChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Public Transport Use',
  content: [
    {
      title: 'Passenger km',
      description:
  <Text fontSize="inherit" mb={2}>
    Average trip length multiplied by total boardings per year. Results only reported for TAs with the presence of a notable public transport network.
  </Text>,
      source: null,
    },
    {
      title: 'Service km',
      description:
  <Text fontSize="inherit" mb={2}>
    The distance travelled by buses, trains and ferries while in-service. Results only reported for TAs with the presence of a notable public transport network.
  </Text>,
      source: null,
    },
    {
      title: 'Public Transport Boardings',
      description:
  <Text fontSize="inherit" mb={2}>
    Expenditure includes for contracted bus, ferry, and rail public transport services that are set out in the current regional public transport plan.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools, use - public transport</Link>
  </Text>,
    },
  ],
};

function PublicTransportUse() {
  const { data: passengerKms, isLoading: isLoadingPassengerKms } = usePassengerKms();
  const { data: serviceKms, isLoading: isLoadingServiceKms } = useServiceKms();
  const { data: boardings, isLoading: isLoadingBoardings } = useBoardings();
  const isLoading = isLoadingPassengerKms || isLoadingServiceKms || isLoadingBoardings;

  // Passenger KMs Chart Data
  const passengerKmsYears = [...new Set(passengerKms?.map((item) => item.Year))];
  const passengerKmsChartData = [
    {
      name: 'Passenger km',
      type: 'line',
      data: passengerKms?.map((item) => ({
        x: item.Year,
        y: item.PassengerKilometresKm,
      })),
      color: CHART_COLOURS.blue,
    },
  ];

  // Service KMs Chart Data
  const serviceKmsYears = [...new Set(serviceKms?.map((item) => item.Year))];
  const serviceKmsChartData = [
    {
      name: 'Service km',
      type: 'line',
      data: serviceKms?.map((item) => ({
        x: item.Year,
        y: item.ServiceKilometresKm,
      })),
      color: CHART_COLOURS.blue,
    },
  ];

  // Boardings Chart Data
  const boardingsYears = [...new Set(boardings?.map((item) => item.Year))];
  const boardingsChartData = [
    {
      name: 'Bus',
      type: 'area',
      data: boardings?.filter((x) => x.PT_Mode === 'Bus').map((item) => ({
        x: item.Year,
        y: item.Value ? item.Value / 1000000 : null,
      })),
      color: CHART_COLOURS.transblue,
    },
    {
      name: 'Rail',
      type: 'area',
      data: boardings?.filter((x) => x.PT_Mode === 'Rail').map((item) => ({
        x: item.Year,
        y: item.Value ? item.Value / 1000000 : null,
      })),
      color: CHART_COLOURS.transorange,
    },
    {
      name: 'Ferry',
      type: 'area',
      data: boardings?.filter((x) => x.PT_Mode === 'Ferry').map((item) => ({
        x: item.Year,
        y: item.Value ? item.Value / 1000000 : null,
      })),
      color: CHART_COLOURS.transgrey,
    },
  ];

  return (
    <ReportSection
      title="Public Transport Use"
      isLoading={isLoading || !passengerKms || !serviceKms || !boardings}
      informationSource={informationSource}
      lockYear={boardings?.[0]?.LockYear || null}
    >
      <Wrap>
        <LineChart
          title="Passenger km"
          categories={passengerKmsYears}
          series={passengerKmsChartData}
          width="220"
          height="280"
          decimalPoints={1}
          yAxisDecimalPoints={1}
        />
        <LineChart
          title="Service km"
          categories={serviceKmsYears}
          series={serviceKmsChartData}
          width="220"
          height="280"
          decimalPoints={2}
          yAxisDecimalPoints={2}
        />
        <StackedAreaChart
          title="Public transport boardings"
          categories={boardingsYears}
          series={boardingsChartData?.filter((x) => x.data?.length > 0)}
          width="220"
          height="280"
          prefix=""
          suffix="M"
          decimalPoints={2}
          yAxisDecimalPoints={2}
        />
      </Wrap>
    </ReportSection>
  );
}

export default PublicTransportUse;
